function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('lp-slider')) {
  class LPSlider extends HTMLElement {
    static rtlScrollBehavior;

    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.viewport = this.querySelector('.lp-slider__viewport');
      this.controls = this.querySelector('.lp-slider__controls');
      this.arrows = this.querySelector('.lp-slider__arrows');
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
      this.dots = this.querySelector('.lp-slider__dots');
      this.rtl = document.dir === 'rtl';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.navigation = this.dataset.navigation || 'arrows_dots';
      this.perViewDesktop = Math.max(1, parseInt(this.dataset.perViewDesktop, 10) || 1);
      this.perViewMobile = Math.max(1, parseInt(this.dataset.perViewMobile, 10) || 1);
      this.currentPage = 0;
      this.scrollHandler = debounce(this.handleScroll.bind(this), 100);
      this.resizeHandler = debounce(this.handleResize.bind(this), 100);
      this.controlsClickHandler = this.handleControlsClick.bind(this);

      if (!this.viewport) return;

      this.refreshSlides();
      this.addEventListeners();
      this.updateLayout(true);

      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
      this.setAttribute('loaded', '');
    }

    disconnectedCallback() {
      this.removeEventListeners();
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    }

    refreshSlides() {
      this.slides = [...this.viewport.children].filter((el) => !el.hasAttribute('hidden'));
      this.slideCount = this.slides.length;
      this.slidesPerView = this.getSlidesPerView();
      this.pageCount = this.slideCount > 0 ? Math.ceil(this.slideCount / this.slidesPerView) : 0;

      // Distance between slide starts; used to compute a consistent page step.
      if (this.slideCount > 1) {
        this.slideSpan = Math.abs(this.slides[1].offsetLeft - this.slides[0].offsetLeft);
      } else {
        this.slideSpan = this.viewport.clientWidth;
      }

      this.pageStep = this.slideSpan * this.slidesPerView;
      if (!this.pageStep || Number.isNaN(this.pageStep)) {
        this.pageStep = this.viewport.clientWidth;
      }
    }

    getSlidesPerView() {
      if (window.matchMedia('(max-width: 768px)').matches) {
        return this.perViewMobile;
      }

      return this.perViewDesktop;
    }

    addEventListeners() {
      this.viewport.addEventListener('scroll', this.scrollHandler, {passive: true});

      if (this.controls) {
        this.controls.addEventListener('click', this.controlsClickHandler);
      }
    }

    removeEventListeners() {
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.scrollHandler);
      }

      if (this.controls) {
        this.controls.removeEventListener('click', this.controlsClickHandler);
      }
    }

    handleControlsClick(evt) {
      if (evt.target.matches('button[name="prev"]')) {
        const direction = this.rtl ? 1 : -1;
        this.goToPage(this.currentPage + direction);
      } else if (evt.target.matches('button[name="next"]')) {
        const direction = this.rtl ? -1 : 1;
        this.goToPage(this.currentPage + direction);
      } else if (evt.target.matches('.lp-slider__dot')) {
        this.goToPage(parseInt(evt.target.dataset.index, 10));
      }
    }

    handleScroll() {
      if (!this.pageCount || !this.viewport.clientWidth) return;

      const nextPage = this.getPageFromScroll();
      if (nextPage === this.currentPage) return;

      this.currentPage = nextPage;
      this.setButtonStates();
      this.updateDots();
    }

    handleResize() {
      this.updateLayout();
    }

    clamp(value, min, max) {
      return Math.max(min, Math.min(max, value));
    }

    getTrackMaxScroll() {
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    }

    getRtlScrollBehavior() {
      if (LPSlider.rtlScrollBehavior) return LPSlider.rtlScrollBehavior;

      const outer = document.createElement('div');
      const inner = document.createElement('div');

      outer.style.width = '4px';
      outer.style.height = '1px';
      outer.style.overflow = 'scroll';
      outer.style.direction = 'rtl';
      outer.style.visibility = 'hidden';
      outer.style.position = 'absolute';
      outer.style.top = '-9999px';

      inner.style.width = '8px';
      inner.style.height = '1px';
      outer.appendChild(inner);
      document.body.appendChild(outer);

      outer.scrollLeft = 0;
      const initial = outer.scrollLeft;
      outer.scrollLeft = 1;
      const afterPositive = outer.scrollLeft;

      if (afterPositive === 0) {
        LPSlider.rtlScrollBehavior = 'negative';
      } else if (initial === 0) {
        LPSlider.rtlScrollBehavior = 'default';
      } else {
        LPSlider.rtlScrollBehavior = 'reverse';
      }

      document.body.removeChild(outer);
      return LPSlider.rtlScrollBehavior;
    }

    getNormalizedScrollLeft() {
      const max = this.getTrackMaxScroll();
      const raw = this.viewport.scrollLeft;

      if (!this.rtl) {
        return this.clamp(raw, 0, max);
      }

      const behavior = this.getRtlScrollBehavior();
      if (behavior === 'negative') {
        return this.clamp(raw * -1, 0, max);
      }

      if (behavior === 'reverse') {
        return this.clamp(max - raw, 0, max);
      }

      return this.clamp(raw, 0, max);
    }

    getScrollLeftFromNormalized(normalizedLeft) {
      const max = this.getTrackMaxScroll();
      const safe = this.clamp(normalizedLeft, 0, max);

      if (!this.rtl) {
        return safe;
      }

      const behavior = this.getRtlScrollBehavior();
      if (behavior === 'negative') {
        return safe * -1;
      }

      if (behavior === 'reverse') {
        return max - safe;
      }

      return safe;
    }

    getPageFromScroll() {
      const normalizedLeft = this.getNormalizedScrollLeft();
      const page = Math.round(normalizedLeft / this.pageStep);
      return this.clampPage(page);
    }

    clampPage(page) {
      if (!this.pageCount) return 0;
      return Math.min(Math.max(page, 0), this.pageCount - 1);
    }

    goToPage(page, behavior = 'smooth') {
      if (this.pageCount <= 1) return;

      const nextPage = this.clampPage(page);
      const targetNormalizedLeft = this.clamp(nextPage * this.pageStep, 0, this.getTrackMaxScroll());

      this.currentPage = nextPage;
      this.updateDots();
      this.setButtonStates();

      this.viewport.scrollTo({
        left: this.getScrollLeftFromNormalized(targetNormalizedLeft),
        behavior: this.reducedMotion ? 'auto' : behavior
      });
    }

    updateDots() {
      if (!this.dots) return;

      this.dots.querySelectorAll('.lp-slider__dot').forEach((dot, index) => {
        if (index === this.currentPage) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    setButtonStates() {
      if (!this.prevBtn || !this.nextBtn) return;

      if (this.rtl) {
        this.prevBtn.disabled = this.currentPage === this.pageCount - 1;
        this.nextBtn.disabled = this.currentPage === 0;
      } else {
        this.prevBtn.disabled = this.currentPage === 0;
        this.nextBtn.disabled = this.currentPage === this.pageCount - 1;
      }
    }

    buildDots() {
      if (!this.dots) return;

      this.dots.innerHTML = '';
      if (this.navigation === 'arrows') return;

      for (let i = 0; i < this.pageCount; i++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'page-btn lp-slider__dot';
        button.dataset.index = String(i);
        button.setAttribute('aria-controls', this.viewport.id);
        if (i === this.currentPage) button.setAttribute('aria-current', 'true');

        button.innerHTML = `
          <span class="visually-hidden">${window.theme?.strings?.sliderLoadSlide || 'Load slide'} ${i + 1} ${window.theme?.strings?.of || 'of'} ${this.pageCount}</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="page-btn__circle">
            <circle class="page-btn__fg-circle" cx="12" cy="12" r="7"></circle>
            <circle class="page-btn__bg-circle" cx="12" cy="12" r="7"></circle>
          </svg>
        `;

        this.dots.appendChild(button);
      }
    }

    updateLayout() {
      this.refreshSlides();

      if (!this.controls) {
        this.classList.remove('lp-slider--has-controls');
        return;
      }

      if (this.pageCount <= 1) {
        this.controls.hidden = true;
        this.classList.remove('lp-slider--has-controls');
        this.currentPage = 0;
        this.viewport.scrollLeft = this.getScrollLeftFromNormalized(0);
        this.updateDots();
        return;
      }

      this.currentPage = this.clampPage(this.currentPage);
      this.buildDots();
      this.updateDots();
      this.setButtonStates();
      this.controls.hidden = false;
      this.classList.add('lp-slider--has-controls');

      this.goToPage(this.currentPage, 'auto');

      if (this.arrows) {
        this.arrows.hidden = this.navigation === 'dots';
      }

      if (this.dots) {
        this.dots.hidden = this.navigation === 'arrows';
      }
    }
  }

  customElements.define('lp-slider', LPSlider);
}

